// OMON-179/177 → reshaped by OMON-183 P1: pure helpers for the planner's
// monitored-event surface. Every value FREEZES AT ADD-TIME onto the plan's
// `_monitoredEvents[]` entry (one snapshot story with the OMON-174 `_prompt`).
//
// OMON-183 (Ed, 2026-06-11): GATES BECAME DEFAULTS. Mandatory and Escalatable
// are plain toggles on every entry — the librarian's OD flags set only the
// DEFAULT the entry seeds with; the planner always gets both controls.
// Targetable died entirely: target lives inline on the recording-method line
// (set/clear per client; `_targetValue` written only while set). The old
// "absent = never granted, explicit false = deactivated" three-state
// semantics are gone — entries always carry `_mandatory` + `_escalate`.

import { roleFields, seedRoles } from "./shift-role-policy";
import { isIncidentKind, partsOf, seedComponentPrompts } from "./event-parts";
import type {
  BuilderMonitoredEvent,
  EscalationDirection,
  MonitoredEventInfo,
  ObservationDefinition,
} from "../types";

/**
 * Where "notify office above N" starts when the librarian has set no default
 * on the fact (Ed, 2026-09-02: was 5).
 *
 * ☠️ 0 means the office is notified on the FIRST occurrence, because
 * escalation fires above the threshold. That is the safe direction for a
 * default: escalation is opt-in (the toggle seeds from the librarian's
 * `_escalatable` flag and is off unless granted), so this number only ever
 * applies once a planner has decided this fact is worth escalating — at which
 * point "tell the office when it happens" is a better starting point than
 * "tell them on the sixth time", which is what 5 quietly meant.
 *
 * ☠️ A ZERO where a truthy number used to be. Every read of this value in the
 * estate is `typeof x === "number"` (TS) or `is int` (Dart) rather than a
 * truthiness check — verified 2026-09-02 across the planner, the plan builder,
 * the client detail renderers and app.carer. Keep it that way: `threshold ||
 * fallback` would silently turn 0 back into the old behaviour.
 */
export const DEFAULT_ESCALATION_THRESHOLD = 0;

/** The librarian's per-fact policy DEFAULTS (live read of the OD flags). */
export interface MonitoredDefaults {
  mandatory: boolean;
  escalatable: boolean;
}

export function defaultsFromOd(od: ObservationDefinition | undefined): MonitoredDefaults {
  return {
    mandatory: od?._mandatory === true,
    escalatable: od?._escalatable === true,
  };
}

/**
 * Seed a builder entry from the catalogue definition at pick time. Toggles
 * seed from the librarian's defaults; the planner tunes per client. Target
 * always seeds OFF — targets are planner-territory (OMON-183 decision 2).
 */
export function seedBuilderMonitoredEvent(
  od: ObservationDefinition,
  subcategory: string | undefined,
): BuilderMonitoredEvent {
  const def = defaultsFromOd(od);
  return {
    odCode: od.name,
    title: od.title,
    subcategory,
    prompt: od._monitoredEventPrompt || "",
    // OMON-232: a fact with parts freezes each part's default prompt too.
    ...(partsOf(od).length ? { componentPrompts: seedComponentPrompts(partsOf(od)) } : {}),
    // ADR-0035 am.8: the roles that owe it, seeded from the librarian's
    // default; `mandatory` is the derived "owed by at least one role".
    shiftRoles: seedRoles(od as unknown as Record<string, unknown>),
    mandatory: seedRoles(od as unknown as Record<string, unknown>).length > 0,
    escalate: def.escalatable,
    escalationThreshold:
      typeof od._escalationThresholdDefault === "number"
        ? od._escalationThresholdDefault
        : DEFAULT_ESCALATION_THRESHOLD,
    escalationDirection: DEFAULT_ESCALATION_DIRECTION,
    targetOn: false,
    targetValue: null,
  };
}

/**
 * ADR-0035 am.9a: read a stored entry's `_allowNotApplicable`. Only an
 * explicit `true` turns it on (absent / anything else = off).
 */
export function readAllowNotApplicable(v: unknown): boolean {
  return v === true;
}

/**
 * Editing a published plan: turn the loaded entry (MonitoredEventInfo, as the
 * loader resolved it) back into a builder row, carrying EVERY tuned value
 * forward — prompt, part prompts, roles, escalation, target and (am.9a) the
 * not-applicable allowance. Re-seeding any of these from the catalogue would
 * silently discard the client's tuning on the next save.
 *
 * ☠️ The save path rebuilds `_monitoredEvents[]` entries field by field
 * (plan-builder `_monitoredEvents: …map`), so a field missing here is a field
 * dropped by ANY edit of the plan. Add new entry fields here, in
 * `monitoredEntryPolicyFields`, in the loader and in draft restore.
 */
export function builderFromPlanEntry(
  m: MonitoredEventInfo,
  od: ObservationDefinition | undefined,
): BuilderMonitoredEvent {
  return {
    odCode: m.odCode,
    title: m.title,
    subcategory: m.subcategory,
    // OMON-174/177: the existing entry's tuned values, never re-seeded.
    prompt: m.prompt || "",
    // OMON-232: the entry's per-part prompts; an entry saved before the fact
    // had parts seeds from the fact's defaults now (frozen on this save).
    ...(partsOf(od).length
      ? { componentPrompts: m.componentPrompts ?? seedComponentPrompts(partsOf(od)) }
      : {}),
    // OMON-183: normalise to plain booleans — pre-reshape entries may lack
    // the fields entirely (old "absent = never granted" semantics), and an
    // undefined here makes the default-chip claim a deviation that isn't one.
    mandatory: m.mandatory === true,
    shiftRoles: m.shiftRoles,
    allowNotApplicable: m.allowNotApplicable === true,
    escalate: m.escalate === true,
    escalationThreshold: typeof m.escalationThreshold === "number" ? m.escalationThreshold : null,
    escalationDirection: m.escalationDirection === "below" ? "below" : "above",
    targetOn: typeof m.targetValue === "number",
    targetValue: typeof m.targetValue === "number" ? m.targetValue : null,
  };
}

/**
 * Which side of the threshold concerns us, when nobody has said (Ed,
 * 2026-09-02). "above" is the historical behaviour, so every plan written
 * before the field keeps its meaning.
 */
export const DEFAULT_ESCALATION_DIRECTION: EscalationDirection = "above";

/**
 * The one place the direction is turned into words.
 *
 * ☠️ Shared on purpose. The planner's control, the client-detail badge and
 * app.carer's row badge all say this, and a carer reading "notifies office
 * above 2" for a fact that actually concerns us when it DROPS is worse than
 * saying nothing — they would record it and move on. One helper means the
 * three surfaces cannot drift apart.
 */
export function escalationPhrase(
  direction: EscalationDirection | undefined,
  threshold: number | null | undefined,
): string {
  const word = direction === "below" ? "below" : "above";
  return typeof threshold === "number"
    ? `Notify office if ${word} ${threshold}`
    : "Notify office if concerned";
}

/**
 * OMON-205: facts that should be present on EVERY new plan by default. The
 * shift log is expected on every plan (every planner wants it), so a fresh
 * draft starts with it already seeded — the planner can still remove it for a
 * specific plan.
 *
 * ☠️ THIS WAS SILENTLY DEAD, and the way it died is the point.
 *
 * It used to be `AUTO_INCLUDED_FACT_CODES = ["daily-log"]`, looked up with
 * `catalogue.get(code)`. But the catalogue is keyed by DOC ID
 * (`loadCatalogue`: `out.set(d.id, data)`), and the definition had since been
 * re-created under a generated id — it now carries `_migratedFrom:
 * "daily-log"` as the only trace of its old name. So the lookup returned
 * undefined, the loop pushed nothing, and **new plans stopped getting a shift
 * log at all**. Nothing failed: the "returns [] for a fact missing from the
 * catalogue" fallback, written so an unpublished fact never throws, is
 * exactly what swallowed it.
 *
 * A stale identifier is worse than an opaque one — meaningful enough to
 * trust, wrong enough to break. So auto-inclusion is now a PROPERTY OF THE
 * DEFINITION (`_autoInclude: true`), set by the librarian on the catalogue
 * doc. Re-creating or renaming a definition carries the flag with it, and no
 * code has to know any fact's name.
 */
export function defaultBuilderMonitoredEvents(
  catalogue: Map<string, ObservationDefinition>,
): BuilderMonitoredEvent[] {
  const out: BuilderMonitoredEvent[] = [];
  // Deterministic order: catalogue `_order` then id, so two planners opening
  // a fresh draft see the same list in the same sequence.
  const auto = [...catalogue.entries()]
    .filter(([, od]) => od._autoInclude === true && od.active !== false)
    .sort(([aId, a], [bId, b]) =>
      (a._order ?? 0) - (b._order ?? 0) || aId.localeCompare(bId));
  for (const [, od] of auto) {
    out.push(seedBuilderMonitoredEvent(od, undefined));
  }
  return out;
}

/** The odCodes every plan must carry (the `_autoInclude` minimum set). */
export function requiredMonitoredOdCodes(
  catalogue: Map<string, ObservationDefinition>,
): Set<string> {
  return new Set(defaultBuilderMonitoredEvents(catalogue).map((m) => m.odCode));
}

/**
 * Every plan carries the minimum set, FIRST — whichever way it reached the
 * builder (Ed, 2026-09-29: "every plan, even those that are copies of old,
 * and predate our new data, must have a shift log entry").
 *
 * Found that day: only "Create plan" seeded it. Editing a published plan
 * copies that plan's events, and resuming a draft restores the draft's, so
 * 16 of 17 production drafts — copies of plans older than the Shift log —
 * lacked it, and nothing at save noticed. So this runs at EVERY way in (edit,
 * resume) AND at every save (draft and publish), and the builder no longer
 * offers × on a required item.
 *
 * - A required item already present KEEPS its tuned entry (prompt, roles,
 *   thresholds) — it is moved to the front, never re-seeded.
 * - A missing one is seeded from the catalogue, exactly as a new plan's is.
 * - Order: required items in catalogue order, then everything else as it was.
 * - Empty catalogue (not yet loaded) → unchanged; the save-time call catches it.
 */
export function ensureMinimumSet(
  events: BuilderMonitoredEvent[],
  catalogue: Map<string, ObservationDefinition>,
): BuilderMonitoredEvent[] {
  const defaults = defaultBuilderMonitoredEvents(catalogue);
  if (defaults.length === 0) return events;
  const required = new Set(defaults.map((d) => d.odCode));
  const present = new Map(
    events.filter((e) => required.has(e.odCode)).map((e) => [e.odCode, e] as const),
  );
  const head = defaults.map((d) => present.get(d.odCode) ?? d);
  const rest = events.filter((e) => !required.has(e.odCode));
  return [...head, ...rest];
}

/**
 * The policy fields to snapshot onto the `_carePlan._monitoredEvents[]` entry
 * at save time. OMON-183: `_mandatory` and `_escalate` are ALWAYS written
 * (plain booleans — readers no longer infer anything from absence).
 * Threshold/target numbers are written only while their control is active
 * and a number is set.
 */
export function monitoredEntryPolicyFields(
  m: BuilderMonitoredEvent,
  od?: ObservationDefinition,
): Record<string, unknown> {
  // ADR-0035 am.8: `_shiftRoles` always written (lock applied), `_mandatory`
  // its derived shadow for readers that predate roles.
  const out: Record<string, unknown> = {
    ...roleFields(m, od as unknown as Record<string, unknown>),
    _escalate: m.escalate === true,
  };
  // ADR-0035 am.9a: written only when on; absent means off. Before the
  // incident early-return, so it is carried for every recording kind.
  if (m.allowNotApplicable === true) out._allowNotApplicable = true;
  // OMON-232 (Ed, 2026-10-01): an incident (ABC chart) has no threshold and
  // no target. Escalating it means THIS incident; the carer's "Notify office"
  // tick is the whole mechanism, so no number is frozen onto the plan.
  if (isIncidentKind(od?._recordingKind)) return out;
  if (m.escalate === true && typeof m.escalationThreshold === "number") {
    out._escalationThreshold = m.escalationThreshold;
    // Written alongside the number, never on its own: a direction with no
    // threshold says nothing, and a threshold with no direction already means
    // "above" by the reader's default.
    out._escalationDirection = m.escalationDirection === "below" ? "below" : "above";
  }
  if (m.targetOn === true && typeof m.targetValue === "number") {
    out._targetValue = m.targetValue;
  }
  return out;
}

/** Compact recording-kind label for picker rows / row meta lines. */
export function recordingKindShortLabel(od: ObservationDefinition | undefined): string {
  switch (od?._recordingKind) {
    case "tally": return "tally / count";
    case "rating": return "rating / numeric";
    case "boolean": return "yes / no";
    case "diary": return "shift log / diary";  // OMON-204
    case "incident": return "incident log (ABC chart)";  // OMON-232
    case "attainment-5":
    default: return "1–5 support scale";
  }
}
